import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Search,
  Bell,
  Package,
  Tag,
  Users,
  Mail,
  TrendingUp,
  ListChecks,
  LogOut,
  ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/hooks/useAuth';
import { Avatar } from '@/components/ui/Avatar';

/* ==========================================================================
   UserMenu — dropdown sull'avatar utente con tutte le pagine dell'app.

   Voci comuni: Home, Cerca cliente, Notifiche.
   Voci admin (visibili a admin/super_admin): Catalogo, Promozioni, Marketing,
   Editor Domande, Gestione Utenti, Obiettivi Fatturato.
   In fondo: Logout.
   ========================================================================== */

interface MenuItem {
  label: string;
  href: string;
  icon: typeof Home;
  adminOnly?: boolean;
}

const MENU_ITEMS: MenuItem[] = [
  { label: 'Home', href: '/', icon: Home },
  { label: 'Cerca cliente', href: '/search', icon: Search },
  { label: 'Notifiche', href: '/notifications', icon: Bell },
  { label: 'Catalogo Servizi', href: '/admin/catalog', icon: Package, adminOnly: true },
  { label: 'Promozioni', href: '/admin/promotions', icon: Tag, adminOnly: true },
  { label: 'Marketing', href: '/admin/marketing', icon: Mail, adminOnly: true },
  { label: 'Editor Domande', href: '/admin/questions', icon: ListChecks, adminOnly: true },
  { label: 'Gestione Utenti', href: '/admin/users', icon: Users, adminOnly: true },
  { label: 'Obiettivi Fatturato', href: '/admin/fatturato', icon: TrendingUp, adminOnly: true },
];

export function UserMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const fullName = user ? `${user.nome} ${user.cognome}`.trim() : '';

  const close = useCallback(() => setIsOpen(false), []);

  // Outside click + Escape closes the menu
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, close]);

  // Close menu on route change
  useEffect(() => {
    close();
  }, [location.pathname, close]);

  if (!user) return null;

  const visibleItems = MENU_ITEMS.filter((it) => !it.adminOnly || isAdmin);

  const handleNavigate = (href: string) => {
    close();
    navigate(href);
  };

  const handleLogout = () => {
    close();
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={`Menu utente ${fullName}`}
        className={cn(
          'inline-flex items-center gap-1.5',
          'h-[var(--touch-min)] pl-1 pr-2 rounded-[var(--radius-full)]',
          'transition-colors duration-[var(--duration-fast)]',
          'hover:bg-surface-sunken active:bg-border-default',
          'cursor-pointer',
        )}
      >
        <Avatar name={fullName || user.email} size="sm" />
        <ChevronDown
          className={cn(
            'h-4 w-4 text-text-secondary shrink-0 transition-transform',
            isOpen && 'rotate-180',
          )}
          strokeWidth={2.5}
        />
      </button>

      {isOpen && (
        <div
          role="menu"
          aria-label="Menu navigazione"
          className={cn(
            'absolute right-0 top-[calc(100%+8px)]',
            'w-64 max-h-[calc(100vh-80px)] overflow-y-auto',
            'bg-surface-card border border-border-default',
            'rounded-[var(--radius-lg)] shadow-lg',
            'z-[var(--z-dropdown,40)]',
            'py-1.5',
          )}
        >
          {/* Header utente */}
          <div className="px-4 py-3 border-b border-border-default">
            <p className="font-[family-name:var(--font-heading)] text-sm font-bold text-text-primary truncate">
              {fullName || 'Utente'}
            </p>
            <p className="text-xs text-text-muted truncate">{user.email}</p>
            <p className="mt-1 text-[10px] uppercase tracking-wide text-text-muted">
              {user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Admin' : 'Operatrice'}
            </p>
          </div>

          {/* Voci di navigazione */}
          <ul className="py-1">
            {visibleItems.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.href;
              return (
                <li key={item.href}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleNavigate(item.href)}
                    className={cn(
                      'flex items-center gap-3 w-full px-4 py-2.5',
                      'text-sm text-text-primary text-left',
                      'transition-colors duration-[var(--duration-fast)]',
                      'hover:bg-surface-sunken cursor-pointer',
                      active && 'bg-surface-sunken font-semibold',
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0 text-text-secondary" strokeWidth={2} />
                    <span className="flex-1 truncate">{item.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Logout */}
          <div className="border-t border-border-default pt-1">
            <button
              type="button"
              role="menuitem"
              onClick={handleLogout}
              className={cn(
                'flex items-center gap-3 w-full px-4 py-2.5',
                'text-sm text-status-negative text-left',
                'transition-colors duration-[var(--duration-fast)]',
                'hover:bg-status-negative-light cursor-pointer',
              )}
            >
              <LogOut className="h-4 w-4 shrink-0" strokeWidth={2} />
              <span className="flex-1">Esci</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
